import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useMentorSession } from '../../context/TrackerContext';
import { WEB_APP_URL } from '../../config';
import { p2pStudentResume, p2pStudents } from '../../lib/api';
import { P2PCard } from '../../crm/P2P';
import type { P2PCase } from '../../crm/crmApi';
import { ReportHeader, HeaderButton, Loading } from '../../components/ReportKit';

// ── Path to Placement: the Career Strategist's students ──────
// The Career Strategist (a mentor account ticked Career Strategist; they take
// no classes) has each P2P student from the test fee on: the test, the result,
// the balance, the ATS offer, a refund, and after the full fee, placement. The
// same card and buttons as the P2P board (crm/P2P.tsx). A student is marked
// when they look stuck (server/routes/p2p.js stuckOf). Mirrors the app's
// P2PStudentsScreen (crm_p2p_screen.dart).

type Tab = 'active' | 'placement' | 'all';
const TABS: { id: Tab; label: string }[] = [{ id: 'active', label: 'In P2P now' }, { id: 'placement', label: 'Placement' }, { id: 'all', label: 'All' }];

const P2PStudents: React.FC = () => {
  const session = useMentorSession();
  const [tab, setTab] = useState<Tab>('active');
  const [data, setData] = useState<Awaited<ReturnType<typeof p2pStudents>> | null>(null);
  const load = useCallback(async () => {
    if (!session.csToken) return;
    setData(null);
    try { setData(await p2pStudents(session.csToken, tab)); } catch (e: any) { toast.error(e.message); }
  }, [session.csToken, tab]);
  useEffect(() => { load(); }, [load]);
  const changed = (c: P2PCase) => setData(d => (d ? { ...d, students: d.students.map(x => (x.id === c.id ? c : x)) } : d));
  const resume = async (s: P2PCase) => {
    try {
      const r = await p2pStudentResume(session.csToken!, s.id);
      window.open(`${WEB_APP_URL}${r.url}`, '_blank', 'noopener');
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <TrackerLayout>
      <div className="max-w-5xl mx-auto space-y-5">
        <ReportHeader icon="🎯" title="P2P Students"
          intro="Every Path to Placement student comes to you once they pay the test fee: set the test time, mark the result, then the balance, the ATS offer or a refund, and placement after the full fee. Their Lead Manager can do these steps too, and you each hear what the other did."
          actions={<HeaderButton onClick={load}>↻ Refresh</HeaderButton>} />
        {!session.csToken ? (
          <p className="text-sm text-gray-600 bg-white rounded-2xl p-4">Sign out and sign in again to see the P2P students.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="P2P students">
              {TABS.map(t => (
                <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold border-2 ${tab === t.id ? 'bg-[#2b2240] text-white border-[#2b2240]' : 'bg-white text-gray-600 border-gray-200'}`}>
                  {t.label}{data ? ` · ${t.id === 'active' ? data.counts.active : t.id === 'placement' ? data.counts.placement : data.counts.all}` : ''}
                </button>
              ))}
              {data && data.counts.stuck > 0 && <span className="px-3 py-2 rounded-xl text-xs font-bold bg-red-100 text-red-700">⚠️ {data.counts.stuck} stuck</span>}
            </div>
            {!data ? <Loading text="Loading the P2P students…" /> : !data.students.length ? (
              <p className="text-sm text-gray-500 bg-white rounded-2xl p-6 text-center">No P2P student here yet.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {data.students.map(s => <P2PCard key={s.id} auth={{ csToken: session.csToken! }} c={s} onChange={changed} openResume={() => resume(s)} />)}
              </div>
            )}
          </>
        )}
      </div>
    </TrackerLayout>
  );
};

export default P2PStudents;
